/**
 * HR admin queue for LGPD/GDPR/CCPA privacy requests.
 *
 * Lists every privacy-request in the org with approve / deny / cancel
 * actions. Separately exposes the optional retention policy
 * (``data_retention_months``) that Sentinel can use to flag terminated
 * employees past the retention window.
 */
import { useEffect, useState } from "react";
import { Card, CardHeader, CardTitle } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Badge } from "@/components/ui/Badge";
import { Select } from "@/components/ui/Select";
import { Input } from "@/components/ui/Input";
import { Modal } from "@/components/ui/Modal";
import { useToast } from "@/hooks/useToast";
import { formatDate } from "@/lib/utils";
import { ShieldCheck, Timer } from "lucide-react";
import {
  actOnPrivacyRequest,
  downloadPrivacyExport,
  listPrivacyRequests,
  type AdminAction,
  type PrivacyRequest,
  type PrivacyRequestStatus,
} from "@/api/privacy";
import {
  getCurrentOrg,
  updateOrg,
} from "@/api/organizations";

const STATUS_BADGE: Record<
  PrivacyRequestStatus,
  { variant: "success" | "warning" | "danger" | "default"; label: string }
> = {
  pending: { variant: "warning", label: "Pending" },
  processing: { variant: "warning", label: "Scheduled" },
  completed: { variant: "success", label: "Completed" },
  denied: { variant: "danger", label: "Denied" },
  cancelled: { variant: "default", label: "Cancelled" },
};

type FilterVal = "open" | "all" | PrivacyRequestStatus;

export function PrivacyRequestsSettings() {
  const { addToast } = useToast();
  const [rows, setRows] = useState<PrivacyRequest[] | null>(null);
  const [filter, setFilter] = useState<FilterVal>("open");
  const [modal, setModal] = useState<{
    req: PrivacyRequest;
    action: AdminAction;
  } | null>(null);
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  const [retentionMonths, setRetentionMonths] = useState<string>("");
  const [savingRetention, setSavingRetention] = useState(false);

  const reload = async () => {
    try {
      const all = await listPrivacyRequests();
      setRows(all);
    } catch {
      addToast("error", "Failed to load privacy requests");
    }
  };

  useEffect(() => {
    reload();
    getCurrentOrg()
      .then((o) => {
        setRetentionMonths(
          o.data_retention_months != null
            ? String(o.data_retention_months)
            : ""
        );
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = (rows || []).filter((r) => {
    if (filter === "all") return true;
    if (filter === "open")
      return r.status === "pending" || r.status === "processing";
    return r.status === filter;
  });

  const openAction = (req: PrivacyRequest, action: AdminAction) => {
    setModal({ req, action });
    setNotes("");
  };

  const confirm = async () => {
    if (!modal) return;
    setSaving(true);
    try {
      await actOnPrivacyRequest(modal.req.id, modal.action, notes.trim() || undefined);
      addToast("success", `Request ${modal.action}d`);
      setModal(null);
      await reload();
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { detail?: string } } };
      addToast("error", ax.response?.data?.detail || "Action failed");
    } finally {
      setSaving(false);
    }
  };

  const onDownload = async (r: PrivacyRequest) => {
    try {
      await downloadPrivacyExport(r.id, `export-${r.id.slice(0, 8)}.json`);
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { detail?: string } } };
      addToast("error", ax.response?.data?.detail || "Download unavailable");
    }
  };

  const saveRetention = async () => {
    setSavingRetention(true);
    try {
      const parsed =
        retentionMonths.trim() === "" ? null : parseInt(retentionMonths, 10);
      if (parsed !== null && (Number.isNaN(parsed) || parsed < 0)) {
        addToast("error", "Retention must be a non-negative number of months");
        setSavingRetention(false);
        return;
      }
      await updateOrg({ data_retention_months: parsed });
      addToast("success", "Retention policy updated");
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { detail?: string } } };
      addToast("error", ax.response?.data?.detail || "Could not save");
    } finally {
      setSavingRetention(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* ─── Retention policy ─────────────────────────────────────── */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Timer className="h-5 w-5" />
            Data retention policy
          </CardTitle>
        </CardHeader>
        <p className="text-sm text-text-muted mb-4">
          Sentinel flags terminated employees whose termination date is older
          than this many months, so HR can review their records for deletion
          under LGPD Art. 16. Leave blank to disable.
        </p>
        <div className="flex items-end gap-3 max-w-md">
          <div className="flex-1">
            <Input
              label="Retention (months after termination)"
              type="number"
              min={0}
              value={retentionMonths}
              onChange={(e) => setRetentionMonths(e.target.value)}
              placeholder="e.g. 36"
            />
          </div>
          <Button onClick={saveRetention} isLoading={savingRetention}>
            Save
          </Button>
        </div>
      </Card>

      {/* ─── Request queue ────────────────────────────────────────── */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5" />
            Privacy requests
          </CardTitle>
        </CardHeader>

        <div className="mb-4 max-w-xs">
          <Select
            value={filter}
            onChange={(e) => setFilter(e.target.value as FilterVal)}
            options={[
              { value: "open", label: "Open (pending + scheduled)" },
              { value: "pending", label: "Pending" },
              { value: "processing", label: "Scheduled" },
              { value: "completed", label: "Completed" },
              { value: "denied", label: "Denied" },
              { value: "cancelled", label: "Cancelled" },
              { value: "all", label: "All" },
            ]}
          />
        </div>

        {!rows ? (
          <p className="text-sm text-text-muted">Loading…</p>
        ) : filtered.length === 0 ? (
          <p className="text-sm text-text-muted">No requests in this view.</p>
        ) : (
          <div className="divide-y divide-border">
            {filtered.map((r) => {
              const badge = STATUS_BADGE[r.status];
              return (
                <div key={r.id} className="py-3 flex items-start gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-text-primary capitalize">
                        {r.request_type}
                      </span>
                      <Badge variant={badge.variant}>{badge.label}</Badge>
                    </div>
                    <p className="text-xs text-text-muted mt-0.5">
                      Submitted {formatDate(r.created_at)}
                      {r.scheduled_for &&
                        ` · Scheduled ${formatDate(r.scheduled_for)}`}
                      {r.expires_at &&
                        ` · Download expires ${formatDate(r.expires_at)}`}
                    </p>
                    {r.reason && (
                      <p className="text-xs text-text-muted mt-1">
                        Reason: {r.reason}
                      </p>
                    )}
                    {r.admin_notes && (
                      <p className="text-xs text-text-muted mt-1 italic">
                        Notes: {r.admin_notes}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {r.has_download && (
                      <Button size="sm" variant="secondary" onClick={() => onDownload(r)}>
                        Download
                      </Button>
                    )}
                    {r.status === "pending" && (
                      <>
                        <Button size="sm" onClick={() => openAction(r, "approve")}>
                          Approve
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => openAction(r, "deny")}
                        >
                          Deny
                        </Button>
                      </>
                    )}
                    {r.status === "processing" && r.request_type === "deletion" && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => openAction(r, "cancel")}
                      >
                        Cancel
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card>

      {/* ─── Action modal ─────────────────────────────────────────── */}
      <Modal
        isOpen={!!modal}
        onClose={() => !saving && setModal(null)}
        title={
          modal
            ? `${modal.action[0].toUpperCase() + modal.action.slice(1)} ${modal.req.request_type} request`
            : ""
        }
      >
        {modal && (
          <div className="space-y-4">
            {modal.action === "approve" && modal.req.request_type === "deletion" && (
              <div className="rounded-lg border border-warning/30 bg-warning/10 p-3 text-sm text-text-primary">
                Approving this will schedule the employee's anonymisation in
                30 days. You can cancel the deletion any time before the
                scheduled date runs.
              </div>
            )}
            {modal.action === "approve" && modal.req.request_type === "export" && (
              <div className="rounded-lg border border-accent/30 bg-accent/10 p-3 text-sm text-text-primary">
                Approving will immediately generate the export. The subject
                can download it for 7 days.
              </div>
            )}
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              placeholder="Optional notes (visible to the requester)"
              className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm"
            />
            <div className="flex justify-end gap-2">
              <Button
                variant="secondary"
                onClick={() => setModal(null)}
                disabled={saving}
              >
                Back
              </Button>
              <Button
                variant={modal.action === "deny" || modal.action === "cancel" ? "danger" : undefined}
                onClick={confirm}
                isLoading={saving}
              >
                Confirm
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
